import { z } from "zod";

/**
 * Commercial SaaS packages and their pure billing math. Two sellable packages;
 * an org is on exactly one plan (organizations.billing_plan):
 *  - "free":      $0 — up to 1000 public passport views per year and 10
 *                 products. Over the view allowance the org's WRITES lock until
 *                 it upgrades (public pages stay open).
 *  - "unlimited": $1000/year flat — unlimited views and products.
 *  - "none":      never billed (on-prem, internal, demo). Not a sellable
 *                 package. The enterprise offer is an on-prem yearly LICENSE,
 *                 not SaaS billing (see @app/core license + docs/on-prem.md).
 *
 * All quantities are per SUBSCRIPTION YEAR = calendar year (UTC). Prices come
 * from config (BILLING_* env vars) — the defaults above are not hardcoded here.
 * Nothing is metered: the only invoice a plan produces is the yearly base fee,
 * so there are no usage/overage invoices (the retired "standard" package was
 * the only metered one — see migration 0027).
 *
 * ESPR/EN 18220 constraint: no limit ever takes the public passport surface
 * offline. Free over-allowance and unpaid invoices lock the authenticated
 * write path only.
 */

export const BILLING_PLANS = ["free", "unlimited", "none"] as const;
export type BillingPlan = (typeof BILLING_PLANS)[number];
export const billingPlanSchema = z.enum(BILLING_PLANS);

export const INVOICE_STATUSES = ["draft", "issued", "paid", "overdue", "void"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];
export const invoiceStatusSchema = z.enum(INVOICE_STATUSES);

/**
 * "subscription" = the yearly base fee. "usage" is legacy: monthly overage
 * invoices from the retired metered package. Nothing issues them any more, but
 * the kind stays so historical invoices keep rendering.
 */
export const INVOICE_KINDS = ["subscription", "usage"] as const;
export type InvoiceKind = (typeof INVOICE_KINDS)[number];
export const invoiceKindSchema = z.enum(INVOICE_KINDS);

/** Money is kept in integer USD cents everywhere to avoid float drift. */
export interface BillingPricing {
  /** Free plan: public views included per year (e.g. 1000). */
  freeIncludedViewsPerYear: number;
  /** Free plan: max products an org may hold (e.g. 10). */
  freeMaxProducts: number;
  /** Unlimited plan: yearly base fee in cents (e.g. 100000 = $1000). */
  unlimitedPriceCents: number;
}

/** Views included per subscription year; null = unlimited (or not metered). */
export function includedViewsPerYear(plan: BillingPlan, pricing: BillingPricing): number | null {
  return plan === "free" ? pricing.freeIncludedViewsPerYear : null;
}

/** Max products the plan allows; null = unlimited. */
export function maxProductsForPlan(plan: BillingPlan, pricing: BillingPricing): number | null {
  return plan === "free" ? pricing.freeMaxProducts : null;
}

/** Yearly base subscription fee in cents (0 for free/none — no invoice at all). */
export function annualFeeCents(plan: BillingPlan, pricing: BillingPricing): number {
  return plan === "unlimited" ? pricing.unlimitedPriceCents : 0;
}

/** Free plan exhaustion: past the yearly allowance the org must upgrade (writes lock). */
export function freeViewLimitExceeded(plan: BillingPlan, ytdViews: number, pricing: BillingPricing): boolean {
  return plan === "free" && ytdViews > pricing.freeIncludedViewsPerYear;
}

/* --- Billing periods --------------------------------------------------------- */

/** Billing-month key "YYYY-MM" for a date (UTC). */
export function monthPeriod(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

/** The month before `now` (UTC) — the default close target on the 1st. */
export function previousMonthPeriod(now: Date): string {
  return monthPeriod(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)));
}

/** Half-open [start, end) UTC bounds for a "YYYY-MM" period. Throws if malformed. */
export function periodRange(period: string): { start: Date; end: Date } {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) throw new Error(`Invalid billing period: ${period}`);
  const year = Number(m[1]);
  const month = Number(m[2]); // 1..12
  if (month < 1 || month > 12) throw new Error(`Invalid billing period: ${period}`);
  return {
    start: new Date(Date.UTC(year, month - 1, 1)),
    end: new Date(Date.UTC(year, month, 1)),
  };
}

/** Subscription-year key "YYYY" for a date (UTC). Also the annual invoice period. */
export function yearPeriod(d: Date): string {
  return String(d.getUTCFullYear());
}

/** The "YYYY" subscription year a "YYYY-MM" billing month belongs to. */
export function yearOfPeriod(period: string): string {
  return period.slice(0, 4);
}

/** Half-open [start, end) UTC bounds for a "YYYY" subscription year. */
export function yearRange(year: string): { start: Date; end: Date } {
  if (!/^\d{4}$/.test(year)) throw new Error(`Invalid billing year: ${year}`);
  const y = Number(year);
  return { start: new Date(Date.UTC(y, 0, 1)), end: new Date(Date.UTC(y + 1, 0, 1)) };
}

export const billingPeriodSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Period must be a YYYY-MM month");

/** Whole-dollar display helper (cents → "$1,234.00" without a locale dep). */
export function formatUsdCents(cents: number): string {
  const dollars = (cents / 100).toFixed(2);
  return `$${dollars}`;
}
